import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { Ability } from 'ember-can';
import type LocationRoleModel from 'garaje/models/location-role';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import { getLegacyRoleName } from 'garaje/utils/custom-roles';
import { GLOBAL_ADMIN, isLocationRole, isCompanyRole, LOCATION_ADMIN, RECEPTIONIST } from 'garaje/utils/roles';
import {
  Permission,
  MANAGE_COMPANY_ADMIN_PERMISSIONS,
  MANAGE_LOCATION_ADMIN_PERMISSIONS,
} from 'garaje/utils/ui-permissions';
import _intersection from 'lodash/intersection';

type RoleScopeEntity = {
  type: 'locations' | 'companies' | 'zones';
  id: string;
  name: string;
};

const CAN_MANAGE_ROLES = [GLOBAL_ADMIN, LOCATION_ADMIN];

const CAN_VISIT_ROLES = [GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST];

const isLocationAdminAtLocation = (currentAdmin: CurrentAdminService, locationId?: string) => {
  return holdsAdminRolesAtLocation(currentAdmin, locationId, [LOCATION_ADMIN]);
};

const holdsAdminRolesAtLocation = (
  currentAdmin: CurrentAdminService,
  locationId: string | undefined,
  adminRoles: string[],
) => {
  return currentAdmin.locationRoles
    .filter((role) => adminRoles.includes(role.roleName))
    .map((role) => role.belongsTo('location').id())
    .includes(locationId!);
};

export default class AdminRoleAbility extends Ability {
  declare model: string;

  @service declare currentAdmin: CurrentAdminService;
  @service declare state: StateService;
  @service declare authz: AuthzService;
  @service declare featureFlags: FeatureFlagsService;

  @tracked role: LocationRoleModel | null = null; // used for admin roles edit dialog

  @tracked roles: Record<string, RoleScopeEntity[]> = {}; // used for admin roles table

  // userId (each user/row in the admins table) are used to stop an
  // admin from editing / deleting their own roles
  @tracked userId = null; // used for admin roles table delete + edit dialog

  get canManage(): boolean {
    if (this.featureFlags.isEnabled('role-assignment-authz-ui')) {
      return this.#canManageWithPermissions();
    }
    return this.#canManageWithLegacyRoles();
  }

  // can the current admin see the edit icon on the admins table
  get canEditRoles(): boolean {
    if (!this.roles) {
      return false;
    }

    if (this.featureFlags.isEnabled('role-assignment-authz-ui')) {
      return this.#canEditRolesWithPermissions();
    }
    return this.#canEditRolesWithLegacyRoles();
  }

  // can the current admin see the delete icon on the admins table
  get canDeleteRoles(): boolean {
    // admins cannot delete themselves
    if (this.userId && this.currentAdmin.id === this.userId) {
      return false;
    }

    if (this.featureFlags.isEnabled('role-assignment-authz-ui')) {
      return this.#canDeleteRolesWithPermissions();
    }
    return this.#canDeleteRolesWithLegacyRoles();
  }

  get canVisit(): boolean {
    const { roleNames } = this.currentAdmin;
    return isPresent(_intersection(CAN_VISIT_ROLES, roleNames));
  }

  get canVisitLocation(): boolean {
    const { roleNames } = this.currentAdmin;
    const locationId = this.model;
    return (
      roleNames.includes(GLOBAL_ADMIN) || holdsAdminRolesAtLocation(this.currentAdmin, locationId, CAN_VISIT_ROLES)
    );
  }

  get canCreateRole(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.USER_MANAGEMENT_ADMIN_CREATE);
  }

  get canUseProperty(): boolean {
    return !!this.state.connectSubscription;
  }

  #canManageWithPermissions(): boolean {
    return (
      this.authz.hasAllPermissionsAtCurrentCompany(MANAGE_COMPANY_ADMIN_PERMISSIONS) ||
      this.authz.hasAllPermissionsAtCurrentLocation(MANAGE_LOCATION_ADMIN_PERMISSIONS)
    );
  }

  #canManageWithLegacyRoles(): boolean {
    const { roleNames } = this.currentAdmin;
    return isPresent(_intersection(CAN_MANAGE_ROLES, roleNames));
  }

  #canEditRolesWithPermissions(): boolean {
    // check if admin can manage company OR location level admins
    const canManageCompanyAdmins = this.authz.hasAllPermissionsAtCurrentCompany(MANAGE_COMPANY_ADMIN_PERMISSIONS);
    const canManageLocationAdmins = this.authz.hasAllPermissionsAtCurrentLocation(MANAGE_LOCATION_ADMIN_PERMISSIONS);

    if (canManageCompanyAdmins || canManageLocationAdmins) {
      return Object.entries(this.roles).some(([roleName, scopeEntities]) => {
        const isCompanyLevelRole = this.#isCompanyLevelRole(roleName, scopeEntities);
        const isLocationLevelRole = this.#isLocationLevelRole(roleName, scopeEntities);

        if (isCompanyLevelRole) {
          // for company roles, admin needs company admin permissions
          return canManageCompanyAdmins;
        } else if (isLocationLevelRole && canManageLocationAdmins) {
          // for location roles, check if admin has permissions at ANY of the locations
          return scopeEntities.some((location) => {
            return this.authz.hasAllPermissionsAtLocation(MANAGE_LOCATION_ADMIN_PERMISSIONS, location.id);
          });
        } else {
          return false;
        }
      });
    }

    return false;
  }

  #isCompanyLevelRole(roleName: string, scopeEntities: RoleScopeEntity[]): boolean {
    // check legacy hardcoded company roles
    const legacyRoleName = getLegacyRoleName(roleName);
    if (isCompanyRole(legacyRoleName)) {
      return true;
    }

    // for custom roles, check the 'type' field in the scope entities
    return scopeEntities.length > 0 && scopeEntities.every((entity) => entity.type === 'companies');
  }

  #isLocationLevelRole(roleName: string, scopeEntities: RoleScopeEntity[]): boolean {
    // check legacy hardcoded location roles
    const legacyRoleName = getLegacyRoleName(roleName);
    if (isLocationRole(legacyRoleName)) {
      return true;
    }

    // for custom roles, check the 'type' field in the scope entities
    return scopeEntities.length > 0 && scopeEntities.every((entity) => entity.type === 'locations');
  }

  #canEditRolesWithLegacyRoles(): boolean {
    const { roleNames } = this.currentAdmin;
    // global admins can edit all other admins
    if (roleNames.includes(GLOBAL_ADMIN)) {
      return true;
    }

    return Object.keys(this.roles).some((roleName) => {
      const scopeEntities = this.roles[roleName];
      const roleNameToCheck = getLegacyRoleName(roleName);

      if (isCompanyRole(roleNameToCheck)) {
        return this.#canEdit(roleNameToCheck);
      } else if (scopeEntities) {
        // check if currentAdmin can manage at least one loc
        return scopeEntities.some((scopeEntity) => {
          return this.#canEdit(roleNameToCheck, scopeEntity.id);
        });
      } else {
        return false;
      }
    });
  }

  #canEdit(roleName: string | null, locationId?: string): boolean {
    const currentAdminRoleNames = this.currentAdmin.roleNames;
    const userId = this.userId;

    if (currentAdminRoleNames.includes(GLOBAL_ADMIN)) {
      // currentAdmin cannot edit their own global admin role
      return !(userId && this.currentAdmin.id === userId && roleName === GLOBAL_ADMIN);
    } else if (currentAdminRoleNames.includes(LOCATION_ADMIN)) {
      if (roleName) {
        // is currentAdmin editing their own roles
        if (userId && this.currentAdmin.id === userId && roleName === LOCATION_ADMIN) {
          // currentAdmin cannot edit their location admin role at the current location
          return !(
            isLocationAdminAtLocation(this.currentAdmin, locationId) && locationId === this.state.currentLocation.id
          );
        } else {
          // currentAdmin is editing another user
          return isLocationRole(roleName) && isLocationAdminAtLocation(this.currentAdmin, locationId);
        }
      } else {
        // role name doesn't exist
        return isLocationAdminAtLocation(this.currentAdmin, locationId);
      }
    }
    return false;
  }

  #canDeleteRolesWithPermissions(): boolean {
    // check if admin can manage company OR location level admins
    const canManageCompanyAdmins = this.authz.hasAllPermissionsAtCurrentCompany(MANAGE_COMPANY_ADMIN_PERMISSIONS);
    const canManageLocationAdmins = this.authz.hasAllPermissionsAtCurrentLocation(MANAGE_LOCATION_ADMIN_PERMISSIONS);

    if (canManageCompanyAdmins || canManageLocationAdmins) {
      return this.canEditRoles;
    }
    return false;
  }

  #canDeleteRolesWithLegacyRoles(): boolean {
    const { roleNames } = this.currentAdmin;
    if (roleNames.includes(GLOBAL_ADMIN)) {
      return true;
    } else if (roleNames.includes(LOCATION_ADMIN)) {
      return this.canEditRoles;
    }
    return false;
  }
}
